import axios from 'axios';
import { LoginResponse } from './auth';

export interface MicrosoftConfiguration {
  enabled: boolean;
  ssoOnly: boolean;
}

export interface MicrosoftAuthError {
  code: string;
  referenceId?: string;
  email?: string;
}

// REACT_APP_HOST_ENV already includes /api in existing deployments.
export function microsoftApiBase(base = process.env.REACT_APP_HOST_ENV || '') {
  const normalized = base.replace(/\/+$/, '');
  return /\/api$/i.test(normalized) ? normalized : `${normalized}/api`;
}

// Deliberately separate from the authenticated client: no Auth interceptor or 401 redirect.
const microsoftClient = axios.create({ baseURL: microsoftApiBase(), timeout: 30000 });
export function getMicrosoftConfiguration(): Promise<MicrosoftConfiguration> {
  // Temporary fixed display mode: Microsoft sign-in only.
  return Promise.resolve({ enabled: true, ssoOnly: true });
}

export function microsoftStartUrl(useDifferentAccount = false) {
  return `${microsoftApiBase()}/auth/microsoft/start${useDifferentAccount ? '?useDifferentAccount=true' : ''}`;
}

export function startMicrosoftSignIn(useDifferentAccount = false) {
  callbackAttempt = null;
  window.location.assign(microsoftStartUrl(useDifferentAccount));
}

export function normalizeMicrosoftError(error: unknown): MicrosoftAuthError {
  const value = (axios.isAxiosError(error) ? error.response?.data : error) as Partial<MicrosoftAuthError> | null;
  return {
    code: typeof value?.code === 'string' ? value.code : 'SIGN_IN_FAILED',
    referenceId: typeof value?.referenceId === 'string' ? value.referenceId : undefined,
    email: typeof value?.email === 'string' ? value.email : undefined,
  };
}

let callbackAttempt: Promise<LoginResponse> | null = null;

export function consumeMicrosoftCallback(): Promise<LoginResponse> {
  const fragment = window.location.hash;
  // Read and scrub before making requests. Retain the in-flight promise across remounts.
  if (fragment) {
    const params = new URLSearchParams(fragment.slice(1));
    window.history.replaceState(window.history.state, '', window.location.pathname);
    if (!callbackAttempt) {
      const error = params.get('error');
      const code = params.get('code');
      if (error) {
        callbackAttempt = Promise.reject({ code: error, referenceId: params.get('referenceId') || undefined });
      } else if (code) {
        callbackAttempt = microsoftClient
          .post<LoginResponse>('/auth/microsoft/exchange', { code }, { withCredentials: true })
          .then(({ data }) => {
            if (!data?.token || !data?.refreshToken || !data?.user) {
              throw new Error('Invalid sign-in response');
            }
            return data;
          });
      }
    }
  }
  if (!callbackAttempt) callbackAttempt = Promise.reject({ code: 'SIGN_IN_FAILED' });
  return callbackAttempt;
}
